import type { FocusEventHandler, KeyboardEventHandler } from 'react';
import { useReducer, useCallback, useRef } from 'react';

import type { CounterValue, HandleQuantityChangeOptions } from '../models';
import { logQuantityPicker as log } from '~/utils/log';

interface UseQuantityPickerOptions {
  canBeDeleted: boolean;
  initialQuantity: number;
  itemCode: string;
  lineNumber: number;
  maxQuantity: number;
  onQuantityChange: (options: HandleQuantityChangeOptions) => Promise<void>;
}

enum ChangeOrigin {
  BlurHandler = 'handleBlur',
  ChangeHandler = 'handleChange',
  KeyDownHandler = 'handleKeyDown',
}

const KEY_ENTER = 'Enter';

export const useQuantityPicker = ({
  canBeDeleted,
  initialQuantity,
  itemCode,
  lineNumber,
  maxQuantity,
  onQuantityChange,
}: UseQuantityPickerOptions) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isInputFocused = useRef(false);
  const isServerUpdate = useRef(false);
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
  const value = useRef<CounterValue>(initialQuantity);
  const setValue = useCallback(
    (newValue: CounterValue) => {
      if (newValue === value.current) {
        log('QuantityPicker.setValue() same value as previous, no change:', newValue);
      } else {
        value.current = newValue;
        log('QuantityPicker.setValue() new value was set:', newValue);
        forceUpdate();
      }
    },
    [value],
  );

  const changeQuantity = useCallback(
    (inputValue: CounterValue, changeOrigin?: ChangeOrigin) => {
      log(
        'QuantityPicker.changeQuantity(), inputValue',
        inputValue,
        ', initialQuantity',
        initialQuantity,
        ', maxQuantity',
        maxQuantity,
        ', change origin:',
        changeOrigin,
      );

      // ignore '-' value completely - server doesn't accept negative values
      if (inputValue === '-') {
        return;
      }

      // non-numeric values
      if (inputValue === '' || Number.isNaN(inputValue)) {
        if (changeOrigin === ChangeOrigin.BlurHandler) {
          // for blur event, let's set some correct numeric value
          setValue(initialQuantity);
        } else {
          // in the middle of the editting process here, just set the local state
          // so that user sees the editted value
          setValue(inputValue);
        }

        return;
      }

      // checks for min and max quantity
      let currentValue = inputValue;

      // just for the case - negative value could be pasted from clipboard for example
      if (currentValue < 0) {
        currentValue = 0;
        log('Current value was set to 0 as negative quantities are not accepted by server.');
      }

      if (currentValue < 1 && !canBeDeleted) {
        currentValue = 1;
        log('Current value was set to 1 as the item cannot be deleted.');
      }

      if (currentValue > maxQuantity) {
        currentValue = maxQuantity;
        log(`Current value was limited to max quantity ${maxQuantity}.`);
      }

      // skip calling updates on server when same as initial quantity
      // skip server updates when the user is edditting the input value directly ATM
      // (the change in handleKeyDown is called only for specific key(s) like Enter,
      // so it should call server for those cases, but the focus still stays in the input)
      if (
        currentValue === initialQuantity ||
        (isInputFocused.current && changeOrigin !== ChangeOrigin.KeyDownHandler)
      ) {
        // just set local state so that user sees the edit updates
        setValue(currentValue);

        return;
      }

      // call server updates - this might return updated value of this item's quantity
      // depending on the checkout rule engine;
      // server operation indication is needed for correct derived value state logic
      isServerUpdate.current = true;
      log('Server update flag was set to true.');

      const resetFlag = () => {
        isServerUpdate.current = false;
        log('Server update flag was set to false.');
      };

      // set local state so that user sees the edit updates;
      // set here so that the isServerUpdate is set up in advance
      setValue(currentValue);

      onQuantityChange({
        itemCode,
        lineNumber,
        quantity: currentValue,
        previousQuantity: initialQuantity,
      }).then(resetFlag);
    },
    [canBeDeleted, initialQuantity, itemCode, lineNumber, maxQuantity, onQuantityChange, setValue],
  );

  // Derived value state after server data updates from shopping rule engine. Stays here for merchandising cases:
  // 1. Quantity of item was increased by previous update of another item's amount.
  // 2. Increasing item quantity over limit increases the qty of another item and original item's qty stays the same.
  if (initialQuantity !== value.current && (!isInputFocused.current || isServerUpdate.current)) {
    log(
      `Setting value to match initialQuantity ${initialQuantity}, value was ${value.current} ...`,
    );
    setValue(initialQuantity);
  }

  const handleFocus = ((event) => {
    isInputFocused.current = true;
    const { target } = event;
    const input = target as HTMLInputElement;
    const { value: currentValue } = input;
    log('QuantityPicker.handleFocus(), currentValue', currentValue);
    // selecting whole text value on focus was requested
    input.setSelectionRange(0, currentValue.length);
  }) as FocusEventHandler<HTMLInputElement>;

  const handleBlur = ((event) => {
    isInputFocused.current = false;
    const { target } = event;
    const input = target as HTMLInputElement;
    const { value: currentValue } = input;
    if (currentValue === '' || currentValue === '-') {
      changeQuantity(currentValue, ChangeOrigin.BlurHandler);
    } else {
      changeQuantity(Number.parseInt(currentValue, 10), ChangeOrigin.BlurHandler);
    }
  }) as FocusEventHandler<HTMLInputElement>;

  const handleKeyDown = ((event) => {
    const { key, target } = event;
    log('QuantityPicker.handleKeyDown() key', key);
    if (typeof target !== 'object' || key !== KEY_ENTER) {
      return;
    }

    const input = target as HTMLInputElement;
    const { value: currentValue } = input;
    const numericValue = Number(currentValue);
    log('QuantityPicker.handleKeyDown() currentValue', currentValue, 'numericValue', numericValue);
    if (currentValue === '' || Number.isNaN(numericValue)) {
      return;
    }
    changeQuantity(numericValue, ChangeOrigin.KeyDownHandler);
  }) as KeyboardEventHandler<HTMLInputElement>;

  const handleChange = useCallback(
    (inputValue: CounterValue) => changeQuantity(inputValue, ChangeOrigin.ChangeHandler),
    [changeQuantity],
  );

  return {
    inputRef,
    isInputFocused: isInputFocused.current,
    isServerUpdate: isServerUpdate.current,
    handleBlur,
    handleChange,
    handleFocus,
    handleKeyDown,
    setValue,
    value: value.current,
  };
};
